import L from 'leaflet'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import PinGate from '../components/PinGate'
import { api } from '../lib/api'
import { nlDagMaand } from '../lib/format'
import { spotShortName } from '../lib/spotLabel'
import type { AdminRegistration, MapMeta, PublicSpot } from '../lib/types'

export default function Kaart() {
  return (
    <PinGate>
      {(token, logout) => <Inner token={token} onLogout={logout} />}
    </PinGate>
  )
}

function firstName(n: string): string {
  return (n || '').trim().split(/\s+/)[0] || ''
}
function esc(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function boundsOf(spots: PublicSpot[]): L.LatLngBounds {
  const b = L.latLngBounds([])
  for (const s of spots) for (const [lng, lat] of s.coordinates) b.extend([lat, lng])
  return b
}

// Split a street's spots into two halves along its principal (long) axis —
// "voor" (first half) and "achter" (second half).
function splitAlongAxis(spots: PublicSpot[]): [PublicSpot[], PublicSpot[]] {
  const cents = spots.map((s) => {
    let x = 0, y = 0
    for (const [lng, lat] of s.coordinates) { x += lng; y += lat }
    return { s, x: x / s.coordinates.length, y: y / s.coordinates.length }
  })
  const n = cents.length
  if (n < 2) return [spots, []]
  let mx = 0, my = 0
  for (const c of cents) { mx += c.x; my += c.y }
  mx /= n; my /= n
  let cxx = 0, cyy = 0, cxy = 0
  for (const c of cents) { const dx = c.x - mx, dy = c.y - my; cxx += dx * dx; cyy += dy * dy; cxy += dx * dy }
  const ang = 0.5 * Math.atan2(2 * cxy, cxx - cyy)
  const ordered = cents
    .map((c) => ({ s: c.s, t: (c.x - mx) * Math.cos(ang) + (c.y - my) * Math.sin(ang) }))
    .sort((a, b) => a.t - b.t)
  const half = Math.ceil(ordered.length / 2)
  return [ordered.slice(0, half).map((p) => p.s), ordered.slice(half).map((p) => p.s)]
}

type Preset = { label: string; bounds: L.LatLngBounds }

// Per-polygon metadata kept around so the betaalstatus-toggle can recolour
// and re-label the spots without rebuilding the whole map.
type SpotLayer = { layer: L.Polygon; spot: PublicSpot; name: string | null; paid: boolean }

function Inner({ token, onLogout }: { token: string; onLogout: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layersRef = useRef<SpotLayer[]>([])
  const [error, setError] = useState<string | null>(null)
  const [presets, setPresets] = useState<Preset[]>([])
  const [showPaid, setShowPaid] = useState(false)
  const [unpaidCount, setUnpaidCount] = useState(0)
  // Editie + marktdag, zodat een uitgeprint blad dat een jaar later nog ergens
  // rondslingert zichzelf identificeert. De lijst komt van dezelfde
  // /admin/registrations-call hieronder, die altijd zegt welke editie ze
  // bediende.
  const [editie, setEditie] = useState<number | null>(null)
  const [editieDatum, setEditieDatum] = useState('')

  useEffect(() => {
    let cancelled = false
    async function run() {
      const [spotsRes, regsRes] = await Promise.all([
        api<{ meta: MapMeta; spots: PublicSpot[] }>('/api/spots'),
        api<{ registrations: AdminRegistration[]; edition?: number; eventDate?: string | null }>(
          '/api/admin/registrations',
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ])
      if (cancelled) return
      if (!spotsRes.ok) { setError('Plekken konden niet geladen worden.'); return }
      if (!regsRes.ok) {
        if (regsRes.status === 401) onLogout()
        setError('Inschrijvingen konden niet geladen worden.'); return
      }
      // Beide uit hetzelfde antwoord: de datum los ophalen (uit `season` op
      // /api/spots) zou twee onafhankelijk bemonsterde "actieve editie"-
      // waarden koppelen, en een heropening tussen die twee requests zet de
      // datum van volgend jaar onder de inschrijvingen van dit jaar.
      setEditie(regsRes.data.edition ?? null)
      setEditieDatum(nlDagMaand(regsRes.data.eventDate))
      const { meta, spots } = spotsRes.data
      const spotToName = new Map<string, string>()
      const spotToPaid = new Map<string, boolean>()
      for (const r of regsRes.data.registrations) for (const id of r.spots) {
        spotToName.set(id, r.naam)
        spotToPaid.set(id, r.betaald)
      }

      if (!containerRef.current || mapRef.current) return
      const map = L.map(containerRef.current, { center: [meta.center[1], meta.center[0]], zoom: meta.zoom })
      mapRef.current = map

      const street = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap', maxZoom: 22,
      })
      const satellite = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { attribution: 'Tiles &copy; Esri', maxZoom: 22, maxNativeZoom: 19 },
      )
      const ortho = L.tileLayer(
        'https://geo.api.vlaanderen.be/OMWRGBMRVL/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=omwrgbmrvl&STYLE=&FORMAT=image/png&TILEMATRIXSET=GoogleMapsVL&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}',
        { attribution: '&copy; Digitaal Vlaanderen — Orthofoto', maxZoom: 22, maxNativeZoom: 21 },
      )
      ortho.addTo(map)
      L.control.layers(
        { 'Luchtfoto (Vlaanderen)': ortho, 'Satelliet (Esri)': satellite, Plattegrond: street },
        undefined,
        { position: 'topright' },
      ).addTo(map)
      L.control.scale({ metric: true, imperial: false, position: 'bottomleft' }).addTo(map)

      const spotLayers: SpotLayer[] = []
      let unpaid = 0
      for (const s of spots) {
        const name = spotToName.get(s.id) || null
        const paid = name ? (spotToPaid.get(s.id) ?? false) : false
        if (name && !paid) unpaid++
        const latlngs = s.coordinates.map(([lng, lat]) => [lat, lng] as [number, number])
        const poly = L.polygon(latlngs, {
          color: '#000', weight: 1,
          fillColor: name ? '#8a8a8a' : '#e5ff73',
          fillOpacity: name ? 0.55 : 0.75,
        })
          .addTo(map)
          // Same adres-label the standhouder sees on /mijn-plek (street name
          // dropped — the blad is already one street), not the internal id.
          .bindTooltip(`${esc(spotShortName(s))} · ${name ? esc(firstName(name)) : 'vrij'}`, {
            permanent: true, direction: 'center', className: 'spot-label', opacity: 1,
          })
        spotLayers.push({ layer: poly, spot: s, name, paid })
      }
      layersRef.current = spotLayers
      setUnpaidCount(unpaid)

      // Zone presets (camera views). Kempstraat is split along its long axis.
      const fluweel = spots.filter((s) => s.zone === 'Fluweelstraat' || s.zone === 'Fluweelstraat Parkkant')
      const tortel = spots.filter((s) => s.zone === 'Tortelduifstraat')
      const kemp = spots.filter((s) => s.zone === 'Kempstraat')
      const [kempVoor, kempAchter] = splitAlongAxis(kemp)
      const ps: Preset[] = []
      if (fluweel.length) ps.push({ label: 'Fluweelstraat', bounds: boundsOf(fluweel) })
      if (tortel.length) ps.push({ label: 'Tortelduifstraat', bounds: boundsOf(tortel) })
      if (kempVoor.length) ps.push({ label: 'Kempstraat voor', bounds: boundsOf(kempVoor) })
      if (kempAchter.length) ps.push({ label: 'Kempstraat achter', bounds: boundsOf(kempAchter) })
      ps.push({ label: 'Alles', bounds: boundsOf(spots) })
      setPresets(ps)

      const all = boundsOf(spots)
      if (all.isValid()) map.fitBounds(all, { padding: [30, 30] })
    }
    run()
    return () => {
      cancelled = true
      mapRef.current?.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Leaflet needs a resize kick when the print layout changes the container size.
  useEffect(() => {
    const kick = () => mapRef.current?.invalidateSize()
    window.addEventListener('beforeprint', kick)
    window.addEventListener('afterprint', kick)
    return () => {
      window.removeEventListener('beforeprint', kick)
      window.removeEventListener('afterprint', kick)
    }
  }, [])

  // Betaalstatus-toggle: recolour bezette plekken (rood = nog te betalen,
  // grijs = betaald) and append a "€" to the label of wie nog moet betalen,
  // so it stays leesbaar op een zwart-wit-afdruk. Off = de gewone kaart.
  useEffect(() => {
    for (const { layer, spot, name, paid } of layersRef.current) {
      const occupied = !!name
      const fillColor = !occupied ? '#e5ff73' : showPaid && !paid ? '#cf2e2e' : '#8a8a8a'
      const fillOpacity = !occupied ? 0.75 : showPaid && !paid ? 0.5 : 0.55
      layer.setStyle({ fillColor, fillOpacity })
      const base = `${esc(spotShortName(spot))} · ${name ? esc(firstName(name)) : 'vrij'}`
      layer.setTooltipContent(showPaid && occupied && !paid ? `${base} · €` : base)
    }
  }, [showPaid, unpaidCount])

  function goTo(p: Preset) {
    if (p.bounds.isValid()) mapRef.current?.fitBounds(p.bounds, { padding: [20, 20] })
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-4">
      <style>{`
        .spot-label {
          background: rgba(255,255,255,.72); border: none; box-shadow: none;
          color: #000; font-size: 10px; line-height: 1.1; font-weight: 600;
          padding: 0 2px; white-space: nowrap;
        }
        .spot-label::before { display: none !important; }
        .kaart-editie { display: none; }
        @media print {
          @page { size: A3 landscape; margin: 0; }
          body * { visibility: hidden !important; }
          .kaart-print, .kaart-print * { visibility: visible !important; }
          .kaart-print { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; margin: 0 !important; }
          .kaart-noprint { display: none !important; }
          .kaart-editie { display: block !important; z-index: 1000; }
        }
      `}</style>

      <div className="kaart-noprint flex flex-wrap items-center gap-2 mb-3">
        <Link to="/beheer" className="text-sm underline text-muted">← Terug naar Beheer</Link>
        <h1 className="font-display text-2xl tracking-display uppercase mr-2">
          Afdrukbare kaart{editie !== null ? ` · ${editie}` : ''}
        </h1>
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => goTo(p)}
            className="px-3 py-1.5 text-sm bg-offwhite border-[3px] border-ink shadow-block-sm font-display tracking-display uppercase hover:translate-y-[1px]"
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setShowPaid((v) => !v)}
          aria-pressed={showPaid}
          className={`px-3 py-1.5 text-sm border-[3px] border-ink shadow-block-sm font-display tracking-display uppercase hover:translate-y-[1px] ${showPaid ? 'bg-red text-offwhite' : 'bg-offwhite text-ink'}`}
        >
          💶 {showPaid
            ? `Betaalstatus aan${unpaidCount > 0 ? ` — ${unpaidCount} te betalen` : ' — alles betaald'}`
            : 'Betaalstatus'}
        </button>
        <button
          onClick={() => window.print()}
          className="px-3 py-1.5 text-sm bg-ink text-lime border-[3px] border-ink shadow-block-sm font-display tracking-display uppercase hover:translate-y-[1px]"
        >
          🖨 Afdrukken
        </button>
        <button onClick={onLogout} className="text-sm text-muted underline ml-auto">Afmelden</button>
      </div>

      {error && <p className="kaart-noprint text-red font-semibold mb-2">{error}</p>}
      <p className="kaart-noprint text-sm text-muted mb-2">
        {showPaid ? (
          <>Betaalstatus aan: <span className="text-red font-semibold">rood = nog te betalen (€)</span>, grijs = betaald, geel = vrij.
          Handig om mee rond te gaan bij het cash ophalen.</>
        ) : (
          <>Grijs = bezet, geel = vrij. Elke plek toont het adres dat de standhouder zelf ziet (zonder straatnaam).
          Zet <strong>Betaalstatus</strong> aan om te zien wie nog moet betalen.</>
        )}
        {' '}Kies een zone, druk dan af (A3 liggend, achtergrond aan) — 4 zones = 4 bladen.
        De schaallat linksonder helpt om ± 4–5 m per plek te checken.
      </p>

      <div className="kaart-print border-[3px] border-ink relative" style={{ height: '78vh' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        {/* In de kaart zelf, want de print-CSS verbergt alles buiten
            .kaart-print. Enkel bij het afdrukken zichtbaar: rechtsboven zit
            op het scherm Leaflets lagenkiezer (ortho / satelliet /
            plattegrond), en die zou hierdoor onbereikbaar worden. Op het
            scherm staat de editie in de titel hierboven; bij het afdrukken
            valt die weg, en dan moet een los blad zichzelf kunnen benoemen.
            Alle vier de hoeken zijn op het scherm bezet door Leaflet-
            controls, dus verplaatsen lost dit niet op. */}
        {editie !== null && (
          <div className="kaart-editie absolute top-2 right-2 bg-offwhite border-[3px] border-ink px-3 py-1 font-display tracking-display uppercase text-sm">
            Rommelmarkt {editie}{editieDatum ? ` · ${editieDatum}` : ''}
          </div>
        )}
      </div>
    </div>
  )
}
